import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { contentUpload } from "../adminApi";
import { resolveMedia } from "@/lib/contentApi";

// Reusable upload control: direct file upload (object storage) + paste-URL fallback.
export default function MediaUploader({ label, value, onChange, accept = "image/*", testId, hint }) {
  const [uploading, setUploading] = useState(false);
  const isVideo = /\.(mp4|webm|mov|m4v)$/i.test(value || "");
  const isPdf = /\.(pdf)$/i.test(value || "");

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await contentUpload(file);
      onChange(url);
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <label className="flex flex-col gap-1.5 text-sm">
      {label && <span className="font-medium text-slate-700">{label}</span>}
      {hint && <span className="text-xs font-normal text-slate-500">{hint}</span>}
      {value && (
        isVideo ? (
          <video src={resolveMedia(value)} controls className="h-28 w-auto rounded-lg bg-black" />
        ) : isPdf ? (
          <a href={resolveMedia(value)} target="_blank" rel="noopener noreferrer" className="w-fit text-xs font-medium text-orange underline">View uploaded file</a>
        ) : (
          <img src={resolveMedia(value)} alt="" className="h-20 w-20 rounded-lg object-cover" />
        )
      )}
      <span className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin text-orange" /> : <Upload className="h-4 w-4" />}
        {uploading ? "Uploading…" : value ? "Replace file" : "Upload file"}
        <input type="file" accept={accept} onChange={handle} disabled={uploading} data-testid={testId} className="hidden" />
      </span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste a public URL"
        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-orange"
      />
    </label>
  );
}
